import type {
  AccessCodesView,
  AdminPlayer,
  AdminPlayersView,
  AdminSkinsView,
  CreatorView,
  MapNotesView,
  PopulationView,
} from '@gardener/protocol';
import { createEffect, createMemo, createSignal, For, type JSX, on, onCleanup, onMount, Show } from 'solid-js';
import { call } from './api';
import { go, PAGE_KEYS, PAGES } from './nav';
import { PlayerBadges } from './players';
import { ago, ask, Icon, type IconName, optimistic, toast } from './ui';

/** A line of the palette: what it says, what it does. */
interface Item {
  group: string;
  label: string;
  icon: IconName;
  /** What else it is found by (an id, a key, a login), and shown dim. */
  hint?: string;
  extra?: JSX.Element;
  run: () => void;
}

/** What the palette knows of the lists it searches, read as it opens (they are small). */
interface Known {
  skins: AdminSkinsView['skins'];
  codes: AccessCodesView['codes'];
  creators: CreatorView[];
  population: PopulationView | null;
  /** Map notes still being worked on (ADR-0223). */
  notes: MapNotesView['notes'];
}

/** Players are looked up once this many characters are typed, this long after the last key (ms). */
const PLAYER_CHARS = 2;
const PLAYER_WAIT_MS = 180;
/** The most lines a group shows. */
const MOST = 6;

const [open, setOpen] = createSignal(false);
/** Opens the palette (the top bar's search, ⌘K, `/`). */
export const openPalette = () => setOpen(true);

const matches = (q: string, ...texts: (string | null | undefined)[]) =>
  !q || texts.some((t) => t?.toLowerCase().includes(q));

/**
 * The command palette (ADR-0154): ⌘K / Ctrl+K (or `/`) anywhere. Pages and actions, players found live by name, id,
 * login, friend code or Twitch, and the skins, access codes and creators by name, and the open map notes by text. Arrows move, Enter runs, Esc closes.
 */
export function Palette(props: { signOut: () => void }) {
  let dialog!: HTMLDialogElement;
  let input!: HTMLInputElement;
  let list!: HTMLDivElement;
  const [q, setQ] = createSignal('');
  const [active, setActive] = createSignal(0);
  const [players, setPlayers] = createSignal<AdminPlayer[]>([]);
  const [searching, setSearching] = createSignal(false);
  const [known, setKnown] = createSignal<Known>({ skins: [], codes: [], creators: [], population: null, notes: [] });

  const onKey = (e: KeyboardEvent) => {
    const typing = e.target instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setOpen(!open());
    } else if (e.key === '/' && !typing && !open()) {
      e.preventDefault();
      setOpen(true);
    }
  };
  onMount(() => addEventListener('keydown', onKey));
  onCleanup(() => removeEventListener('keydown', onKey));

  createEffect(
    on(open, (isOpen) => {
      if (!isOpen) return dialog.open && dialog.close();
      setQ('');
      setPlayers([]);
      setActive(0);
      dialog.showModal();
      input.focus();
      // Small lists, read fresh each time: a code just made is found.
      void Promise.all([
        call<AdminSkinsView>('GET', '/api/admin/skins').catch(() => ({ skins: [] })),
        call<AccessCodesView>('GET', '/api/admin/access-codes').catch(() => ({ codes: [] })),
        call<{ creators: CreatorView[] }>('GET', '/api/admin/creators').catch(() => ({ creators: [] })),
        call<PopulationView>('GET', '/api/admin/population').catch(() => null),
        call<MapNotesView>('GET', '/api/admin/map-notes?status=open,question,fixed').catch(() => ({ notes: [] })),
      ]).then(([s, c, cr, p, n]) =>
        setKnown({ skins: s.skins, codes: c.codes, creators: cr.creators, population: p, notes: n.notes }),
      );
    }),
  );

  // Players, looked up as you type (the latest answer wins).
  let asked = 0;
  createEffect(
    on(q, (text) => {
      const term = text.trim();
      const mine = ++asked;
      if (term.length < PLAYER_CHARS) {
        setPlayers([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      const timer = setTimeout(async () => {
        const found = await call<AdminPlayersView>('GET', `/api/admin/players?q=${encodeURIComponent(term)}`).catch(
          () => ({ players: [] }),
        );
        if (mine !== asked) return;
        setPlayers(found.players);
        setSearching(false);
      }, PLAYER_WAIT_MS);
      onCleanup(() => clearTimeout(timer));
    }),
  );

  const close = () => setOpen(false);
  const run = (item: Item) => {
    close();
    item.run();
  };

  const items = createMemo<Item[]>(() => {
    const term = q().trim().toLowerCase();
    const k = known();
    const pop = k.population;
    const pages = PAGE_KEYS.filter((p) => matches(term, PAGES[p].title, p)).map<Item>((p) => ({
      group: 'Pages',
      label: PAGES[p].title,
      icon: PAGES[p].icon,
      hint: PAGES[p].blurb,
      run: () => go(p),
    }));
    const all: Item[] = [
      { group: 'Actions', label: 'New access code', icon: 'plus', run: () => go('codes', 'new') },
      { group: 'Actions', label: 'Approve a creator', icon: 'twitch', run: () => go('creators', 'new') },
      ...(pop
        ? [
            {
              group: 'Actions',
              label: pop.settings.on ? 'Turn the population off' : 'Turn the population on',
              icon: 'power' as const,
              run: () => void population(pop),
            },
          ]
        : []),
      { group: 'Actions', label: 'Sign out', icon: 'logout', run: props.signOut },
    ];
    const actions = all.filter((a) => matches(term, a.label));
    const found = players().map<Item>((p) => ({
      group: 'Players',
      label: p.name,
      icon: 'user',
      hint: p.login ? `@${p.login}` : `seen ${ago(p.lastSeenAt)}`,
      extra: <PlayerBadges p={p} />,
      run: () => go('players', p.id),
    }));
    const skins = term
      ? k.skins
          .filter((s) => matches(term, s.name, s.key))
          .map<Item>((s) => ({
            group: 'Skins',
            label: s.name,
            icon: 'sparkles',
            hint: s.preview ? (s.live ? `${s.kind} · in preview · live` : `${s.kind} · in preview`) : s.id,
            run: () => go('skins', s.id),
          }))
      : [];
    const codes = term
      ? k.codes
          .filter((c) => matches(term, c.code, c.label))
          .map<Item>((c) => ({
            group: 'Access codes',
            label: c.code,
            icon: 'key',
            hint: `${c.label} · ${c.kind}`,
            run: () => go('codes', c.key),
          }))
      : [];
    const creators = term
      ? k.creators
          .filter((c) => matches(term, c.name, c.twitch))
          .map<Item>((c) => ({
            group: 'Creators',
            label: c.name,
            icon: 'twitch',
            hint: c.twitch,
            run: () => go('players', c.id),
          }))
      : [];
    const notes = term
      ? k.notes
          .filter((n) => matches(term, `m${n.id}`, n.text, n.map))
          .map<Item>((n) => ({
            group: 'Map notes',
            label: `M${n.id} · ${n.text.length > 60 ? `${n.text.slice(0, 60)}…` : n.text}`,
            icon: 'map',
            hint: `${n.map} · ${n.status}`,
            run: () => go('maps', `${n.map}/${n.id}`),
          }))
      : [];
    return [found, pages, actions, skins, creators, notes, codes].flatMap((g) => g.slice(0, MOST));
  });

  createEffect(on(items, () => setActive(0)));
  createEffect(() => {
    const n = active();
    list?.querySelector(`[data-i="${n}"]`)?.scrollIntoView({ block: 'nearest' });
  });

  const onInputKey = (e: KeyboardEvent) => {
    const n = items().length;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (n) setActive((active() + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items()[active()];
      if (item) run(item);
    }
  };

  return (
    // logdash's modal (`Modal.svelte`, the elevated surface) holding a search field over rows drawn as the sidebar's
    // (`SidebarMenuItem`: its column's px-3, a row's h-7.5 px-2 gap-1.5), the field's icon and text on the rows'.
    <dialog
      ref={dialog}
      class="fixed inset-0 z-999 m-0 flex h-full max-h-none w-full max-w-none overflow-y-auto border-0 bg-transparent p-4 backdrop:bg-transparent [&:not([open])]:hidden"
      aria-label="Search"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <div class="bg-surface-elevated-bg border-surface-elevated-border relative z-10 mx-auto mt-[10vh] mb-auto flex max-h-[min(36rem,80dvh)] w-full max-w-xl flex-col rounded-xl border transition-[scale,opacity] duration-200 ease-in-out motion-reduce:transition-none starting:scale-95 starting:opacity-0">
        <label class="relative mx-3 mt-3 block h-9 shrink-0">
          <span class="sr-only">Search</span>
          <Icon
            name="search"
            class="text-fg-muted pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
          />
          <input
            ref={input}
            class="ld-input py-0 pr-9 pl-7.5"
            value={q()}
            placeholder="Search players, pages, skins, codes…"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={items().length ? `palette-${active()}` : undefined}
            onInput={(e) => setQ(e.currentTarget.value)}
            onKeyDown={onInputKey}
          />
          <Show when={searching()}>
            <span
              class="ld-spinner text-fg-muted absolute top-1/2 right-3 -translate-y-1/2"
              role="status"
              aria-label="Searching"
              data-size="xs"
            />
          </Show>
        </label>
        <div
          ref={list}
          id="palette-list"
          role="listbox"
          class="flex min-h-40 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pt-2 pb-3"
        >
          <For
            each={items()}
            fallback={
              <p class="text-fg-muted my-auto py-6 text-center text-sm">
                {q().trim().length >= PLAYER_CHARS && !searching()
                  ? `Nothing found for “${q().trim()}”`
                  : 'Type to search'}
              </p>
            }
          >
            {(item, i) => (
              <>
                <Show when={i() === 0 || items()[i() - 1]?.group !== item.group}>
                  <div
                    class="text-fg-tertiary flex h-7.5 shrink-0 items-center px-2 text-[13px] font-medium not-first:mt-3"
                    role="presentation"
                  >
                    {item.group}
                  </div>
                </Show>
                {/* The search field's keys move and pick (`aria-activedescendant`); the pointer clicks. A picked row is
                    the elevated surface's hairline grey, so its badges take surface-150's hairline or they vanish. */}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: the combobox's input handles the keys. */}
                <div
                  id={`palette-${i()}`}
                  role="option"
                  tabIndex={-1}
                  class="text-fg-tertiary aria-selected:bg-surface-elevated-hover-bg aria-selected:text-fg-default flex h-7.5 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-2 text-sm font-medium select-none pointer-coarse:h-9 aria-selected:[--edge-color:var(--surface-150-border)]"
                  data-i={i()}
                  aria-selected={active() === i()}
                  onMouseMove={() => setActive(i())}
                  onClick={() => run(item)}
                >
                  <Icon name={item.icon} class="size-4 shrink-0" />
                  <span class="shrink-0 truncate">{item.label}</span>
                  {item.extra}
                  <Show when={item.hint}>
                    <span class="text-fg-muted ml-auto min-w-0 truncate pl-2 text-xs font-normal">{item.hint}</span>
                  </Show>
                </div>
              </>
            )}
          </For>
        </div>
        <div class="edge-t text-fg-muted flex shrink-0 items-center gap-4 px-5 py-3 text-xs">
          <span class="flex items-center gap-1.5">
            <kbd class="ld-kbd" data-size="sm">
              ↑
            </kbd>
            <kbd class="ld-kbd" data-size="sm">
              ↓
            </kbd>
            move
          </span>
          <span class="flex items-center gap-1.5">
            <kbd class="ld-kbd" data-size="sm">
              ↵
            </kbd>
            open
          </span>
          <span class="flex items-center gap-1.5">
            <kbd class="ld-kbd" data-size="sm">
              esc
            </kbd>
            close
          </span>
        </div>
      </div>
      <div
        class="bg-surface-root-bg/60 fixed inset-0 transition-opacity duration-200 ease-in-out motion-reduce:transition-none starting:opacity-0"
        aria-hidden="true"
        onClick={close}
      />
    </dialog>
  );
}

/** The population on or off from the palette: asked first, as on its page. */
async function population(view: PopulationView): Promise<void> {
  const on = !view.settings.on;
  const sure = await ask({
    title: on ? 'Turn the population on?' : 'Turn the population off?',
    body: on
      ? 'Its bots fill Battle Royale rooms as players, and PLAY shows the crowd again.'
      : 'Battle Royale rooms fill with the ladder’s bots, and PLAY shows only the players really on.',
    confirm: on ? 'Turn on' : 'Turn off',
    danger: !on,
  });
  if (sure === null) return;
  await optimistic(
    () => () => {},
    () => call<PopulationView>('PUT', '/api/admin/population', { ...view.settings, on }),
    () => toast(on ? 'The population is on' : 'The population is off'),
  );
}
